import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

const ShiftContext = createContext(null);
const SHIFT_KEY = 'niva_shift';

export function ShiftProvider({ children }) {
  // Default on — start-shift bar was removed, so off-shift would lock the app.
  const [isOnShift, setIsOnShift] = useState(() => {
    try {
      const stored = localStorage.getItem(SHIFT_KEY);
      if (stored === null) return true;
      return stored === 'true';
    } catch {
      return true;
    }
  });

  const startShift = useCallback(() => {
    setIsOnShift(true);
    try {
      localStorage.setItem(SHIFT_KEY, 'true');
    } catch {
      // ignore
    }
  }, []);

  const endShift = useCallback(() => {
    setIsOnShift(false);
    try {
      localStorage.setItem(SHIFT_KEY, 'false');
    } catch {
      // ignore
    }
  }, []);

  // Unstick sessions that were left off-shift after the start button was removed.
  useEffect(() => {
    if (!isOnShift) startShift();
    // Only on mount — do not re-run after an intentional End Shift in-session.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = useMemo(
    () => ({ isOnShift, startShift, endShift }),
    [isOnShift, startShift, endShift]
  );

  return <ShiftContext.Provider value={value}>{children}</ShiftContext.Provider>;
}

export function useShift() {
  const ctx = useContext(ShiftContext);
  if (!ctx) throw new Error('useShift must be used within ShiftProvider');
  return ctx;
}
